import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Button,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	CircularProgress,
	Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { FornecedorService } from "../../service/fornecedor_service";

// Conteúdo da aba "Fornecedores" dentro da página de Solicitações.
// onCountChange (opcional) reporta a quantidade de pendentes ao componente pai.
function SolicitacoesFornecedor({ onCountChange }) {
	const navigate = useNavigate();
	const [solicitacoes, setSolicitacoes] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, item: null });
	const [processando, setProcessando] = useState(false);
	const [feedback, setFeedback] = useState(null);

	const unidadeDistribuidoraId = localStorage.getItem("selected_unidade_id");

	const loadSolicitacoes = useCallback(async () => {
		setLoading(true);
		try {
			const service = new FornecedorService();
			// Traz o histórico completo (todos os status); o badge conta só os pendentes.
			const data = await service.getSolicitacoes(unidadeDistribuidoraId);
			setSolicitacoes(data || []);
			if (onCountChange)
				onCountChange((data || []).filter((s) => s.status === "PENDENTE").length);
		} catch (error) {
			console.error("Erro ao carregar solicitações:", error);
			setFeedback({ type: "error", message: error.message || "Erro ao carregar solicitações" });
		} finally {
			setLoading(false);
		}
	}, [unidadeDistribuidoraId, onCountChange]);

	useEffect(() => {
		loadSolicitacoes();
	}, [loadSolicitacoes]);

	const formatCpfCnpj = (value) => {
		if (!value) return "";
		const clean = value.replace(/\D/g, "");
		if (clean.length <= 11)
			return clean.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
		return clean.replace(/(\d{2})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1/$2").replace(/(\d{4})(\d{1,2})$/, "$1-$2");
	};

	const handleAceitar = (item) => {
		setConfirmDialog({ open: true, action: "aceitar", item });
	};

	const handleRecusar = (item) => {
		setConfirmDialog({ open: true, action: "recusar", item });
	};

	const handleConfirm = async () => {
		const { action, item } = confirmDialog;
		setProcessando(true);
		try {
			const service = new FornecedorService();
			if (action === "aceitar") {
				// NÃO marca como aceita aqui. A solicitação só vira ACEITO depois
				// que o fornecedor for efetivamente salvo no cadastro (FornecedoresCad).
				setConfirmDialog({ open: false, action: null, item: null });
				navigate("/fornecedores/cadastro", {
					state: {
						fromSolicitacao: true,
						solicitacaoId: item.id,
						propriedadeOrigemId: item.propriedadeOrigemId || null,
						dadosPreenchidos: {
							nome: item.nome,
							nomeFantasia: item.nomeFantasia || "",
							cpfCnpj: formatCpfCnpj(item.cpfCnpj),
							email: item.email || "",
							telefone: item.telefone || "",
							cep: item.cep || "",
							uf: item.uf || "",
							cidade: item.cidade || "",
							bairro: item.bairro || "",
							logradouro: item.logradouro || "",
						},
					},
				});
			} else {
				await service.recusarSolicitacao(item.id);
				setConfirmDialog({ open: false, action: null, item: null });
				setFeedback({ type: "success", message: "Solicitação recusada." });
				loadSolicitacoes();
			}
		} catch (error) {
			setFeedback({ type: "error", message: error.message || "Erro ao processar solicitação" });
		} finally {
			setProcessando(false);
		}
	};

	const paginatedData = solicitacoes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	const statusInfo = {
		PENDENTE: { label: "Pendente", color: "warning" },
		ACEITO: { label: "Aceito", color: "success" },
		RECUSADO: { label: "Recusado", color: "error" },
	};

	const formatDateTime = (value) => {
		if (!value) return "-";
		return new Date(value).toLocaleString("pt-BR", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	return (
		<>
			<Box>
				{feedback && (
					<Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
						{feedback.message}
					</Alert>
				)}

				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
						<CircularProgress />
					</Box>
				) : solicitacoes.length === 0 ? (
					<Alert severity="info">Nenhuma solicitação.</Alert>
				) : (
					<Paper>
						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell><strong>Nome</strong></TableCell>
										<TableCell><strong>CPF/CNPJ</strong></TableCell>
										<TableCell><strong>Email</strong></TableCell>
										<TableCell><strong>Telefone</strong></TableCell>
										<TableCell><strong>Cidade/UF</strong></TableCell>
										<TableCell><strong>Status</strong></TableCell>
										<TableCell><strong>Respondido em</strong></TableCell>
										<TableCell align="center"><strong>Ações</strong></TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{paginatedData.map((item) => {
										const info = statusInfo[item.status] || statusInfo.PENDENTE;
										return (
										<TableRow key={item.id} hover>
											<TableCell>{item.nome}</TableCell>
											<TableCell>{formatCpfCnpj(item.cpfCnpj)}</TableCell>
											<TableCell>{item.email || "-"}</TableCell>
											<TableCell>{item.telefone || "-"}</TableCell>
											<TableCell>{[item.cidade, item.uf].filter(Boolean).join("/") || "-"}</TableCell>
											<TableCell>
												<Chip label={info.label} color={info.color} size="small" />
											</TableCell>
											<TableCell>{formatDateTime(item.respondidoEm)}</TableCell>
											<TableCell align="center">
												{item.status === "PENDENTE" ? (
													<Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
														<Button
															size="small"
															variant="contained"
															color="success"
															startIcon={<CheckCircleIcon />}
															onClick={() => handleAceitar(item)}
														>
															Aceitar
														</Button>
														<Button
															size="small"
															variant="outlined"
															color="error"
															startIcon={<CancelIcon />}
															onClick={() => handleRecusar(item)}
														>
															Recusar
														</Button>
													</Box>
												) : (
													<Box sx={{ textAlign: "center", color: "text.disabled" }}>—</Box>
												)}
											</TableCell>
										</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							component="div"
							count={solicitacoes.length}
							page={page}
							onPageChange={(_, newPage) => setPage(newPage)}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
							labelRowsPerPage="Linhas por página"
						/>
					</Paper>
				)}
			</Box>

			<Dialog open={confirmDialog.open} onClose={() => !processando && setConfirmDialog({ open: false, action: null, item: null })}>
				<DialogTitle>
					{confirmDialog.action === "aceitar" ? "Aceitar Solicitação" : "Recusar Solicitação"}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{confirmDialog.action === "aceitar"
							? `Deseja aceitar a solicitação de "${confirmDialog.item?.nome}"? Você será redirecionado para o cadastro de fornecedor com os dados pré-preenchidos.`
							: `Deseja recusar a solicitação de "${confirmDialog.item?.nome}"?`}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDialog({ open: false, action: null, item: null })} disabled={processando}>
						Cancelar
					</Button>
					<Button
						onClick={handleConfirm}
						variant="contained"
						color={confirmDialog.action === "aceitar" ? "success" : "error"}
						disabled={processando}
					>
						{processando ? <CircularProgress size={20} /> : confirmDialog.action === "aceitar" ? "Aceitar" : "Recusar"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

export default SolicitacoesFornecedor;
